'use client';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  queryTomVendingRejectSummary,
  type TomVendingRejectSummaryRow,
} from '@/hooks/useTomVendingInventory';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';

function defaultDateRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

type LocRow = { id: number; name?: string; isWarehouse?: boolean };

const TomsVendingInventoryRejectSummaryPage: React.FC = () => {
  const { showError } = useNotification();
  const { data: locations = [], isLoading: locLoading } = useTomVendingLocationsList();
  const init = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(init.dateFrom);
  const [dateTo, setDateTo] = useState(init.dateTo);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [rows, setRows] = useState<TomVendingRejectSummaryRow[]>([]);
  const [totalRejected, setTotalRejected] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await queryTomVendingRejectSummary({
        dateFrom,
        dateTo,
        locationId: locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId),
      });
      setRows(r.rows);
      setTotalRejected(r.totalRejectedUnits);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load reject summary';
      showError(msg);
      setRows([]);
      setTotalRejected(0);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, locationId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Inventory', href: '/app/toms-vending/inventory' },
    { label: 'Vending reject totals' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Vending inventory — reject totals" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Aggregated units removed from vending machines as inventory rejects (Tom’s vending ledger). This is
          separate from cash or service rejects elsewhere in the app.{' '}
          <RouterLink to="/app/toms-vending/reports/rejects">
            For route and warehouse rejects with visit context, see the Reject report.
          </RouterLink>
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 260 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => setLocationId(v?.id ?? '')}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, l) => (
              <li {...props} key={l.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{l.name?.trim() || `Location ${l.id}`}</Typography>
                  {tomVendingLocationIsWarehouse(l) ? (
                    <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
                  ) : null}
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Location (optional)" />}
          />
          <Button variant="contained" size="small" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
          <Button
            component={RouterLink}
            to="/app/toms-vending/inventory/movements"
            variant="text"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Stock movements
          </Button>
        </Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Total rejected units: <strong>{totalRejected}</strong>
        </Typography>
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>UPC</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>From location</TableCell>
                <TableCell>From machine</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>Loading…</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>No rows in this range.</TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={`${r.inventoryItemId}-${r.fromLocationId ?? 'x'}-${r.fromVendingMachineId ?? 'y'}`}
                  >
                    <TableCell>{r.upc}</TableCell>
                    <TableCell>{r.itemName || '—'}</TableCell>
                    <TableCell align="right">{r.totalQty}</TableCell>
                    <TableCell>{r.fromLocationId ?? '—'}</TableCell>
                    <TableCell>{r.fromVendingMachineId ?? '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default TomsVendingInventoryRejectSummaryPage;
